/**
 * CoachCard — the first-challenge tour's anchored explanation card (design variant 1a).
 * Presentational: the overlay owns measurement + absolute positioning and passes `style`, the arrow
 * direction, and the arrow's horizontal offset. Shows Coco (any mascot node), title, body, "n / N"
 * progress, "Skip tour", and on the right either a "Got it ›" pill (coach beat) or a pulsing
 * "● Tap the highlight" hint (action beat).
 * See specs/first-challenge-onboarding/spec.md (Display Requirements).
 */

import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { View, Text, Pressable, Animated, Easing, type StyleProp, type ViewStyle } from 'react-native';
import { coachStyles as s } from './CoachCard.styles';
import { COACH_COPY, COACH_TEST_IDS } from './CoachCard.constants';

export interface CoachCardProps {
  title: string;
  text: string;
  progress?: { index: number; total: number } | null;
  /** 'action' → pulsing "Tap the highlight" hint; 'coach' → "Got it ›" button. */
  beatType: 'action' | 'coach';
  onSkip: () => void;
  onGotIt?: () => void;
  /** Mascot node (AnimatedMonkey in the app; a placeholder in stories). */
  avatar?: ReactNode;
  /** Arrow pointing at the target; 'none' centers the card with no arrow. */
  arrow?: 'up' | 'down' | 'none';
  /** Arrow horizontal offset from the card's left edge (px). Defaults to centered. */
  arrowOffset?: number;
  /** Absolute positioning from the overlay. */
  style?: StyleProp<ViewStyle>;
  /** Disable the pulsing dot (reduce-motion). */
  reduceMotion?: boolean;
  testID?: string;
}

export function CoachCard({
  title, text, progress, beatType, onSkip, onGotIt, avatar,
  arrow = 'none', arrowOffset, style, reduceMotion = false, testID,
}: CoachCardProps) {
  const pulse = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (beatType !== 'action' || reduceMotion) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 0.35, duration: 700, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 1, duration: 700, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => { loop.stop(); pulse.setValue(1); };
  }, [beatType, reduceMotion, pulse]);

  const arrowStyle = useMemo<StyleProp<ViewStyle>>(() => {
    if (arrow === 'none') return null;
    const dir = arrow === 'up' ? s.arrowUp : s.arrowDown;
    const left = arrowOffset != null ? { left: arrowOffset } : { left: '50%' as const, marginLeft: -8 };
    return [s.arrow, dir, left];
  }, [arrow, arrowOffset]);

  return (
    <View style={[s.card, style]} testID={testID ?? COACH_TEST_IDS.CARD}>
      {arrowStyle ? <View style={arrowStyle} pointerEvents="none" /> : null}
      <View style={s.row}>
        <View style={s.avatar}>{avatar}</View>
        <View style={s.bodyCol}>
          <View style={s.headerRow}>
            <Text style={s.title} numberOfLines={1}>{title}</Text>
            {progress ? (
              <Text style={s.progress}>{progress.index} / {progress.total}</Text>
            ) : null}
          </View>
          <Text style={s.text}>{text}</Text>
          <View style={s.footerRow}>
            <Pressable
              onPress={onSkip}
              testID={COACH_TEST_IDS.SKIP}
              accessibilityRole="button"
              accessibilityLabel={COACH_COPY.skip}
              hitSlop={8}
            >
              <Text style={s.skip}>{COACH_COPY.skip}</Text>
            </Pressable>
            {beatType === 'coach' ? (
              <Pressable
                onPress={onGotIt}
                testID={COACH_TEST_IDS.GOTIT}
                accessibilityRole="button"
                accessibilityLabel={COACH_COPY.gotIt}
                style={({ pressed }) => [s.gotItBtn, pressed && s.pressed]}
              >
                <Text style={s.gotItText}>{COACH_COPY.gotIt}</Text>
              </Pressable>
            ) : (
              <View style={s.hintRow} testID={COACH_TEST_IDS.HINT}>
                <Animated.View style={[s.hintDot, { opacity: reduceMotion ? 1 : pulse }]} />
                <Text style={s.hintText}>{COACH_COPY.tapHint}</Text>
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
}
